"use client";

import React, { useEffect, useRef, useState } from "react";
import { Leaf, Activity, PersonStanding, HeartPulse, Compass, CircleCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
function useRevealOnScroll<T extends HTMLElement = HTMLElement>(threshold = 0.1) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(node);
      }
    }, {
      threshold
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);
  return {
    ref,
    isVisible
  };
}
const tools = [{
  title: "Hypnosystemik",
  shortDesc: "Synergie aus Erickson’scher Hypnotherapie und systemischem Denken zur Reaktivierung unbewusster Kompetenz-Netzwerke.",
  longDesc: "Basierend auf den Konzepten von Dr. Gunther Schmidt nutzen wir die Kraft des Unbewussten, um einschränkende 'Problem-Trancen' aufzulösen. Anstatt Defizite zu analysieren, reaktivieren wir blockierte Kompetenzmuster. Ziel ist eine autonome Lösungsgestaltung, bei der Ressourcen so vernetzt werden, dass Veränderungen nicht nur kognitiv verstanden, sondern als intuitive Erleichterung erlebt werden.",
  benefits: ["Transformation von Problem-Trancen", "Aktivierung unbewusster Potentiale", "Erhalt der Autonomie"],
  icon: Leaf
}, {
  title: "Systemische Lösungsorientierung",
  shortDesc: "Fokus auf Ressourcen und Kontextanalyse, um nachhaltige Verhaltensänderungen in komplexen Systemen zu initiieren.",
  longDesc: "Wir betrachten Schwierigkeiten als Lösungsversuche innerhalb komplexer Lebenszusammenhänge. Durch zirkuläres Fragen und die Analyse funktionaler Ausnahmen identifizieren wir bereits vorhandene Stärken. Dieser Ansatz stärkt Ihre Selbstwirksamkeit und ermöglicht es, neue, gesündere Interaktionsmuster im sozialen und beruflichen Feld stabil zu etablieren.",
  benefits: ["Zirkuläre Kontextanalyse", "Stärkung der Selbstwirksamkeit", "Identifikation von Ausnahmen"],
  icon: Activity
}, {
  title: "Embodiment",
  shortDesc: "Neurobiologische Regulation psychischer Prozesse über körperliche Feedbackschleifen und somatische Verankerung.",
  longDesc: "Psychische Prozesse sind untrennbar in physiologische Zustände eingebettet. Unter Einbeziehung somatischer Marker nutzen wir die Weisheit des Körpers (Bottom-up), um das Nervensystem direkt zu regulieren. Durch gezielte Bewegungs- und Haltungsinterventionen werden mentale Fortschritte physisch repräsentiert und dadurch tiefer und nachhaltiger im Nervensystem gespeichert.",
  benefits: ["Vagus-Nerv-Regulation", "Somatische Ressourcenverankerung", "Direkte Stressminderung"],
  icon: PersonStanding
}, {
  title: "Achtsamkeit",
  shortDesc: "Schulung metakognitiver Beobachtung zur neurobiologischen Selbstregulation und Unterbrechung von Reiz-Reaktions-Ketten.",
  longDesc: "Jenseits bloßer Entspannung dient Achtsamkeit der Entwicklung einer wertfreien Beobachterinstanz gegenüber eigenen Gedanken und Affekten. Diese metakognitive Kompetenz unterbricht automatisierte Stress-Kaskaden. Sie ermöglicht psychische Flexibilität und Stabilität, indem sie den Raum zwischen Reiz und Reaktion vergrößert – für bewusstes Handeln statt blindem Reagieren.",
  benefits: ["Metakognitive Flexibilität", "Emotionsregulation", "Präsenz in Belastungssituationen"],
  icon: HeartPulse
}, {
  title: "Naturbasierte Interventionen",
  shortDesc: "Nutzung des Naturraums als externer Regulator und projektiver Raum für tiefgreifende Transformationsprozesse.",
  longDesc: "Die Wildnispädagogik nutzt die biophile Resonanz des Soonwaldes, um das sympathische Nervensystem unmittelbar zu regulieren. Wir nutzen Naturphänomene als lebendige, archetypische Metaphern für Ihre inneren Prozesse. Die Natur fungiert hier als Co-Therapeut, der eine Erdung und Rekalibrierung Ihrer persönlichen Kompassnadel in einem geschützten, weiten Raum ermöglicht.",
  benefits: ["Biophile Stressreduktion", "Erdung und Zentrierung", "Projektive Erkenntnisprozesse"],
  icon: Compass
}];
function ToolCard({
  tool,
  index
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll(0.1);
  const Icon = tool.icon;
  return <Dialog>
      <DialogTrigger asChild>
        <div ref={ref} className={`cursor-pointer transition-all duration-1000 ease-out delay-${index * 150} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="flex flex-col items-center group p-8 rounded-3xl hover:bg-accent/5 transition-all duration-500 border border-transparent hover:border-primary/10">
            <div className="w-24 h-24 rounded-full bg-accent/30 flex items-center justify-center mb-8 transition-all duration-700 group-hover:bg-primary/10 group-hover:scale-110">
              <Icon className="w-10 h-10 text-primary/70 group-hover:text-primary transition-colors" strokeWidth={1} />
            </div>
            
            <h3 className="text-2xl font-default font-normal text-foreground mb-6 tracking-wide uppercase text-center">
              {tool.title}
            </h3>
            
            <div className="w-8 h-[1px] bg-primary/30 mb-6 group-hover:w-16 transition-all duration-500" />
            
            <p className="text-center text-muted-foreground leading-relaxed font-default font-light text-[17px]">
              {tool.shortDesc}
            </p>
            
            <span className="mt-6 text-xs font-mono text-primary/40 uppercase tracking-[0.2em] group-hover:text-primary transition-colors">
              Details vertiefen
            </span>
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-2xl bg-background/95 backdrop-blur-xl border-primary/20 rounded-[2rem] p-8 md:p-12 selection:bg-primary/20">
        <DialogHeader>
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-6">
            <Icon className="w-8 h-8 text-primary" strokeWidth={1} />
          </div>
          <DialogTitle className="text-3xl md:text-4xl font-default font-light tracking-wider text-foreground uppercase mb-4">
            {tool.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-8">
          <p className="text-lg md:text-xl text-foreground/80 leading-relaxed font-default font-light italic border-l-2 border-primary/20 pl-6">
            {tool.longDesc}
          </p>

          <div className="grid grid-cols-1 gap-4 pt-4">
            <h4 className="text-sm font-mono uppercase tracking-[0.2em] text-primary">Ihre Benefits</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {tool.benefits.map((benefit, i) => <div key={i} className="flex items-center gap-3 text-muted-foreground font-default text-sm bg-accent/20 p-3 rounded-xl">
                  <CircleCheck className="w-4 h-4 text-primary/60" />
                  {benefit}
                </div>)}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}
export default function ToolsForYourStrength() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="mein-ansatz-methoden" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} aria-hidden="true" />

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-primary font-mono text-xs tracking-[0.3em] uppercase mb-4 block">Methodische Expertise</span>
          <h2 className="text-4xl md:text-5xl font-default font-light text-foreground mb-6 uppercase tracking-wider leading-tight">
            Methodische <span className="italic text-primary">Präzision</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default leading-relaxed font-light">Wirksamkeit entsteht, wenn wissenschaftlich fundierte Konzepte auf intuitive Begleitung treffen. Erfahre hier mehr über die Ansätze, die ich dir anbiete.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
          {tools.map((tool, i) => <ToolCard key={tool.title} tool={tool} index={i} />)}
        </div>
      </div>
    </section>;
}